import { useEffect, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import {
  Package, Truck, ShoppingBag, Hammer, Plus, House, Info, Tag,
} from "@phosphor-icons/react";
import PageHeader from "../components/PageHeader";

export default function ClientPriceList() {
  const { t } = useI18n();
  const [pl, setPl] = useState(null);
  const [tab, setTab] = useState("products");

  const SHIPPING_ZONES = [
    { key: "IT",       label: t("zone_it"),       flag: "🇮🇹" },
    { key: "EU_CONT",  label: t("zone_eu_cont"),  flag: "🇪🇺" },
    { key: "EU_NORTH", label: t("zone_eu_north"), flag: "🇸🇪" },
    { key: "UK_CH",    label: t("zone_uk_ch"),    flag: "🇬🇧" },
    { key: "US",       label: t("zone_us"),       flag: "🇺🇸" },
    { key: "ROW",      label: t("zone_row"),      flag: "🌍" },
  ];

  const TIERS = [
    { label: t("pl_tier_1_3") },
    { label: t("pl_tier_4_6") },
    { label: t("pl_tier_7_10") },
    { label: t("pl_tier_11_plus") },
  ];

  useEffect(() => {
    api.get("/client/pricelist").then(({ data }) => setPl(data));
  }, []);

  if (!pl) {
    return <div className="text-[var(--tf-muted)]" data-testid="loading">{t("loading")}</div>;
  }

  return (
    <div className="space-y-6" data-testid="client-pricelist">
      <PageHeader
        eyebrow={t("nav_pricelist")}
        title={t("pl_page_title")}
        subtitle={t("pl_page_subtitle")}
        chip="tf-chip-peach"
        icon={Tag}
      />

      <div className="tf-card overflow-hidden">
        <div className="flex gap-1 px-3 md:px-5 pt-3 md:pt-4 border-b border-[var(--tf-border)] bg-[var(--tf-bg)]/40 overflow-x-auto scroll-thin">
          <TabButton active={tab === "products"} onClick={() => setTab("products")} icon={Package}     label={t("pl_tab_products")} testid="tab-products" />
          <TabButton active={tab === "shipping"} onClick={() => setTab("shipping")} icon={Truck}       label={t("pl_tab_shipping")} testid="tab-shipping" />
          <TabButton active={tab === "premade"}  onClick={() => setTab("premade")}  icon={ShoppingBag} label={t("pl_tab_premade")}  testid="tab-premade" />
        </div>

        <div className="p-5">
          {tab === "products" && <ProductsView items={pl.items || []} t={t} />}
          {tab === "shipping" && <ShippingView zones={pl.shipping_zones || {}} t={t} zonesList={SHIPPING_ZONES} tiers={TIERS} />}
          {tab === "premade"  && <PremadeView picking={pl.picking || {}} t={t} />}
        </div>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-3 md:px-4 py-2.5 font-display font-bold text-xs md:text-sm border-b-2 transition-colors -mb-px whitespace-nowrap ${
        active
          ? "border-[var(--tf-primary)] text-[var(--tf-ink)] bg-white rounded-t-lg"
          : "border-transparent text-[var(--tf-muted)] hover:text-[var(--tf-ink)]"
      }`}
    >
      <Icon size={16} weight="bold" />
      {label}
    </button>
  );
}

/* ---------- Products view (read-only, grouped by product family) ---------- */
function cleanLabel(label) {
  // Remove cosmetic suffixes used in single-side items (e.g. "Tazza Classic - stampa circolare")
  return (label || "")
    .replace(/\s*-\s*1\s*lato$/i, "")
    .replace(/\s*-\s*2\s*lati$/i, "")
    .replace(/\s*-\s*stampa\s+frontale$/i, "")
    .replace(/\s*-\s*stampa\s+circolare$/i, "")
    .replace(/\s*-\s*stampa\s+interno\s+collo$/i, "")
    .trim();
}

function groupProducts(items) {
  // Multi-variant items follow `<base>-1side|-2sides|-neck`. Single-side items (bags, caps, mugs)
  // have a flat code and one of these suffixes in the label only. We merge both into one table
  // exposing columns: Prodotto / 1 lato / 2 lati / Tag.
  const VARIANT_SUFFIXES = [
    { suffix: "-2sides", key: "two"  },
    { suffix: "-1side",  key: "one"  },
    { suffix: "-neck",   key: "neck" },
  ];

  const groups = new Map();
  const order = [];

  const ensure = (base) => {
    if (!groups.has(base)) {
      groups.set(base, { base, name: null, one: null, two: null, neck: null });
      order.push(base);
    }
    return groups.get(base);
  };

  for (const it of items) {
    const code = (it.code || "").toLowerCase();
    const match = VARIANT_SUFFIXES.find((v) => code.endsWith(v.suffix));
    if (match) {
      const base = code.slice(0, -match.suffix.length);
      const g = ensure(base);
      g[match.key] = it.price;
      const cl = cleanLabel(it.label);
      if (!g.name || cl.length > g.name.length) g.name = cl;
    } else {
      // Single-side product (one row in catalog). Treat it as a "1 lato" entry.
      const g = ensure(code);
      g.one = it.price;
      g.name = cleanLabel(it.label) || code;
    }
  }

  return order.map((k) => groups.get(k));
}

function ProductsView({ items, t }) {
  const groups = groupProducts(items);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="tf-label flex items-center gap-2"><Package size={12} weight="bold" /> {t("pl_products_title")}</div>
          <p className="text-xs text-[var(--tf-muted)] mt-1.5 max-w-2xl">
            {t("pl_products_sub")}
          </p>
        </div>
        <div className="text-right">
          <div className="font-display font-black text-2xl tf-numeric">{groups.length}</div>
          <div className="tf-label">{t("pl_products_count")}</div>
        </div>
      </div>

      <div className="tf-card overflow-hidden">
        <div className="hidden md:block">
          <table className="tf-table">
            <thead>
              <tr>
                <th>{t("pl_col_product")}</th>
                <th className="text-right">
                  <div>{t("pl_col_1side")}</div>
                  <div className="text-[9px] tracking-wider opacity-70 normal-case font-normal mt-0.5">{t("pl_vat_excluded")}</div>
                </th>
                <th className="text-right">
                  <div>{t("pl_col_2sides")}</div>
                  <div className="text-[9px] tracking-wider opacity-70 normal-case font-normal mt-0.5">{t("pl_vat_excluded")}</div>
                </th>
                <th className="text-right">
                  <div>{t("pl_col_neck")}</div>
                  <div className="text-[9px] tracking-wider opacity-70 normal-case font-normal mt-0.5">{t("pl_col_neck_sub")}</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {groups.length === 0 && (
                <tr><td colSpan={4} className="text-center text-[var(--tf-muted)] py-8">{t("pl_no_catalog")}</td></tr>
              )}
              {groups.map((g) => (
                <tr key={g.base} data-testid={`prod-${g.base}`}>
                  <td className="text-sm">{g.name}</td>
                  <PriceCell value={g.one} />
                  <PriceCell value={g.two} />
                  <PriceCell value={g.neck} muted />
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile: card list per prodotto con 3 mini-price */}
        <div className="md:hidden divide-y divide-[var(--tf-border)]" data-testid="products-mlist">
          {groups.length === 0 && (
            <div className="text-center text-[var(--tf-muted)] py-8 text-sm">{t("pl_no_catalog")}</div>
          )}
          {groups.map((g) => (
            <div key={g.base} className="p-4 space-y-2.5" data-testid={`prod-card-${g.base}`}>
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-[#F5F2E8] text-[var(--tf-ink)] flex items-center justify-center flex-shrink-0">
                  <Package size={16} weight="fill" />
                </div>
                <div className="font-display font-bold text-sm truncate flex-1">{g.name}</div>
              </div>
              <div className="grid grid-cols-3 gap-2 pl-11">
                <MobilePriceBox label={t("pl_col_1side").split(" ")[0]} value={g.one} />
                <MobilePriceBox label={t("pl_col_2sides").split(" ")[0]} value={g.two} />
                <MobilePriceBox label={t("pl_col_neck").split(" ")[0]} value={g.neck} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PriceCell({ value, muted = false }) {
  if (value == null || value === 0) {
    return <td className="text-right text-[var(--tf-muted)] text-xs">—</td>;
  }
  return (
    <td className={`text-right tf-numeric whitespace-nowrap ${muted ? "font-medium text-[var(--tf-ink-soft)]" : "font-bold"}`}>
      € {Number(value).toFixed(2)}
    </td>
  );
}

function MobilePriceBox({ label, value }) {
  const isEmpty = value == null || value === 0;
  return (
    <div className={`rounded-xl border ${isEmpty ? "border-dashed border-[var(--tf-border)] bg-[var(--tf-bg)]/40" : "border-[var(--tf-border)] bg-white"} p-2 text-center`}>
      <div className="text-[9px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold">{label}</div>
      <div className={`font-display font-black text-sm tf-numeric mt-0.5 ${isEmpty ? "text-[var(--tf-muted)]" : "text-[var(--tf-ink)]"}`}>
        {isEmpty ? "—" : `€${Number(value).toFixed(2)}`}
      </div>
    </div>
  );
}

/* ---------- Shipping view (read-only) ---------- */
function ShippingView({ zones, t, zonesList, tiers }) {
  return (
    <div className="space-y-4">
      <div>
        <div className="tf-label flex items-center gap-2"><Truck size={12} weight="bold" /> {t("pl_shipping_title")}</div>
        <p className="text-xs text-[var(--tf-muted)] mt-1.5 max-w-2xl">
          {t("pl_shipping_sub")}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {zonesList.map((z) => {
          const zoneTiers = zones[z.key] || [];
          return (
            <div key={z.key} className="tf-card overflow-hidden" data-testid={`zone-${z.key}`}>
              <div className="p-4 border-b border-[var(--tf-border)] flex items-center gap-3 bg-[var(--tf-bg)]/40">
                <div className="text-2xl">{z.flag}</div>
                <div>
                  <div className="tf-label">{t("pl_zone_label")} · {z.key}</div>
                  <div className="font-display font-bold text-base">{z.label}</div>
                </div>
              </div>
              <table className="tf-table tf-table-compact">
                <thead>
                  <tr>
                    <th style={{ width: "60%" }}>{t("pl_col_tier")}</th>
                    <th style={{ width: "40%", textAlign: "right" }}>
                      <div>{t("pl_col_price")}</div>
                      <div className="text-[9px] tracking-wider opacity-70 normal-case font-normal mt-0.5">{t("pl_vat_excluded")}</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tiers.map((tier, ti) => (
                    <tr key={ti}>
                      <td className="text-sm">{tier.label}</td>
                      <td className="text-right tf-numeric font-bold whitespace-nowrap">
                        € {Number(zoneTiers[ti]?.price ?? 0).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>

      <div className="tf-card p-4 bg-[var(--tf-bg)]/50 flex items-start gap-3" data-testid="ship-info">
        <Info size={18} weight="duotone" className="shrink-0 mt-0.5 text-[var(--tf-muted)]" />
        <div className="text-xs text-[var(--tf-muted)] leading-relaxed">
          {t("pl_shipping_info")}
        </div>
      </div>
    </div>
  );
}

/* ---------- Premade view (read-only) ---------- */
function PremadeView({ picking, t }) {
  const p = {
    first: Number(picking.first ?? 1.22),
    additional: Number(picking.additional ?? 0.61),
    cap: Number(picking.cap ?? 12.20),
  };
  const calc = (qty) => {
    if (qty <= 0) return 0;
    return Math.min(p.first + (qty - 1) * p.additional, p.cap);
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="tf-label flex items-center gap-2"><ShoppingBag size={12} weight="bold" /> {t("pl_premade_title")}</div>
        <p className="text-xs text-[var(--tf-muted)] mt-1.5 max-w-2xl">
          {t("pl_premade_sub")}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ReadField label={t("pl_first_item")} sublabel={t("pl_vat_excluded")} value={p.first} icon={Hammer} testid="pick-first" />
        <ReadField label={t("pl_additional_item")} sublabel={t("pl_additional_item_sub")} value={p.additional} icon={Plus} testid="pick-additional" />
        <ReadField label={t("pl_cap_label")} sublabel={t("pl_cap_sub")} value={p.cap} icon={House} testid="pick-cap" />
      </div>

      <div className="tf-card overflow-hidden">
        <div className="p-4 border-b border-[var(--tf-border)] bg-[var(--tf-bg)]/40">
          <div className="tf-label">{t("pl_preview_title")}</div>
          <div className="text-xs text-[var(--tf-muted)] mt-1">
            {t("pl_preview_sub")}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[var(--tf-border)]">
          {[1, 3, 10, 25].map((qty) => (
            <div key={qty} className="p-4 text-center" data-testid={`preview-${qty}`}>
              <div className="tf-label">{qty} {qty === 1 ? t("pl_item_singular") : t("pl_item_plural")}</div>
              <div className="font-display font-black text-2xl mt-1.5 tf-numeric">€ {calc(qty).toFixed(2)}</div>
              {calc(qty) >= p.cap && qty > 1 && (
                <div className="text-[10px] text-[var(--tf-muted)] mt-1">{t("pl_cap_reached")}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ReadField({ label, sublabel, value, icon: Icon, testid }) {
  return (
    <div className="tf-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[var(--tf-muted)]">
        <Icon size={16} weight="bold" />
        <div className="tf-label">{label}</div>
      </div>
      <div className="text-xs text-[var(--tf-muted)] mt-1">{sublabel}</div>
      <div className="flex items-baseline gap-2 mt-3">
        <span className="text-[var(--tf-muted)] font-display font-bold text-lg">€</span>
        <span className="font-display font-black text-2xl tf-numeric">{Number(value).toFixed(2)}</span>
      </div>
    </div>
  );
}
